'use client';
import * as React from 'react';

import { AppRoutes } from 'config';

import { Breadcrumb } from '../PageContainer';
import { RouterContext, NavigationContext } from '../shared/context';
import { matchPath, getItemPath, getItemTitle } from '../shared/navigation';

export interface ActivePage {
  path: string;
  title: string;
  breadcrumbs: Breadcrumb[];
}

export function useActivePage(): ActivePage | null {
  const navigationContext = React.useContext(NavigationContext);
  const routerContext = React.useContext(RouterContext);
  const pathname = routerContext?.pathname ?? AppRoutes.Home;
  const activeItem = matchPath(navigationContext, pathname);

  const rootItem = matchPath(navigationContext, AppRoutes.Home);

  return React.useMemo(() => {
    if (!activeItem) {
      return null;
    }

    const breadcrumbs: Breadcrumb[] = [];

    if (rootItem) {
      breadcrumbs.push({
        path: AppRoutes.Home,
        title: getItemTitle(rootItem),
      });
    }

    const segments = pathname.split('/').filter(Boolean);
    let prefix = '';
    for (const segment of segments) {
      const path = `${prefix}/${segment}`;
      prefix = path;
      const item = matchPath(navigationContext, path);
      if (!item) {
        continue;
      }
      const itemPath = getItemPath(navigationContext, item);
      const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
      if (lastCrumb?.path !== itemPath) {
        breadcrumbs.push({
          path: itemPath,
          title: getItemTitle(item),
        });
      }
    }

    return {
      breadcrumbs,
      title: getItemTitle(activeItem),
      path: getItemPath(navigationContext, activeItem),
    };
  }, [activeItem, rootItem, pathname, navigationContext]);
}
